import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import { signOut } from "firebase/auth";
import { FiCopy, FiCheck, FiLogOut, FiMail, FiUser, FiShield, FiCalendar, FiClock } from "react-icons/fi";

// ── helpers ──────────────────────────────────────────────────────
function formatDate(dateStr) {
    if (!dateStr) return "—";
    return new Intl.DateTimeFormat("en-US", {
        day: "numeric", month: "short", year: "numeric",
        hour: "numeric", minute: "2-digit", hour12: true,
    }).format(new Date(dateStr));
}

const PROVIDER_META = {
    "google.com":  { label: "Google",           color: "#4285f4", bg: "#4285f418", border: "#4285f440" },
    "password":    { label: "Email / Password",  color: "#2a9d8f", bg: "#2a9d8f18", border: "#2a9d8f40" },
    "github.com":  { label: "GitHub",            color: "#24292e", bg: "#24292e12", border: "#24292e30" },
    "apple.com":   { label: "Apple",             color: "#000",    bg: "#00000010", border: "#00000030" },
};

// ── Detail row ───────────────────────────────────────────────────
function DetailRow({ icon, label, children, isLast }) {
    return (
        <div style={{
            display: "flex", alignItems: "flex-start", gap: 14,
            padding: "14px 0",
            borderBottom: isLast ? "none" : "1px solid #f3f3f3",
        }}>
            <div style={{
                width: 34, height: 34, borderRadius: 10, flexShrink: 0,
                background: "#2a9d8f12", color: "#2a9d8f",
                display: "flex", alignItems: "center", justifyContent: "center",
            }}>
                {icon}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                    fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                    letterSpacing: "0.07em", color: "#aaa", marginBottom: 3,
                }}>
                    {label}
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#000", wordBreak: "break-all" }}>
                    {children}
                </div>
            </div>
        </div>
    );
}

export default function Profile() {
    const navigate = useNavigate();
    const user = auth.currentUser;
    const [copied, setCopied] = useState(false);
    const [signingOut, setSigningOut] = useState(false);

    const displayName  = user?.displayName || "Admin";
    const email        = user?.email || "No email";
    const uid          = user?.uid || "";
    const photoURL     = user?.photoURL || null;
    const emailVerified = !!user?.emailVerified;
    const provider     = user?.providerData?.[0]?.providerId ?? "password";
    const createdAt    = formatDate(user?.metadata?.creationTime);
    const lastSignIn   = formatDate(user?.metadata?.lastSignInTime);

    const providerMeta = PROVIDER_META[provider] || { label: provider, color: "#606060", bg: "#f0f0f0", border: "#E0E0E0" };

    const initials = (displayName || "A").split(" ").map(w => w[0] || "").join("").slice(0, 2).toUpperCase();
    const [avatarErr, setAvatarErr] = useState(false);

    const handleCopy = () => {
        if (!uid) return;
        navigator.clipboard.writeText(uid).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
        }).catch(() => {});
    };

    const handleSignOut = async () => {
        setSigningOut(true);
        await signOut(auth);
        navigate("/login");
    };

    return (
        <div style={{ fontFamily: "'Quicksand', sans-serif", maxWidth: 560 }}>

            {/* ── Page header ── */}
            <h1 style={{ marginBottom: 24 }}>My Profile</h1>

            {/* ── Hero card ── */}
            <div style={{
                background: "#fff", borderRadius: 20,
                border: "1px solid #E0E0E0",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                overflow: "hidden", marginBottom: 16,
            }}>
                {/* Teal banner */}
                <div style={{ height: 80, background: "linear-gradient(135deg, #2a9d8f 0%, #3ab4a5 100%)" }} />

                {/* Avatar + identity */}
                <div style={{ padding: "0 28px 24px", marginTop: -40 }}>
                    <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 14 }}>
                        {/* Avatar */}
                        {photoURL && !avatarErr ? (
                            <img
                                src={photoURL}
                                alt={displayName}
                                onError={() => setAvatarErr(true)}
                                style={{
                                    width: 76, height: 76, borderRadius: "50%", objectFit: "cover",
                                    border: "4px solid #fff",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                                }}
                            />
                        ) : (
                            <div style={{
                                width: 76, height: 76, borderRadius: "50%",
                                background: "#2a9d8f", color: "#fff",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: 28, fontWeight: 700,
                                border: "4px solid #fff",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                                flexShrink: 0,
                            }}>
                                {initials}
                            </div>
                        )}
                    </div>

                    <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.2, marginBottom: 4 }}>
                        {displayName}
                    </div>
                    <div style={{ fontSize: 14, color: "#606060", fontWeight: 500, marginBottom: 12 }}>
                        {email}
                    </div>

                    {/* Badges */}
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        <span style={{
                            display: "inline-flex", alignItems: "center", gap: 5,
                            background: "#3a82c618", color: "#1565c0",
                            border: "1px solid #3a82c640",
                            borderRadius: 999, padding: "3px 12px",
                            fontSize: 11, fontWeight: 700,
                        }}>
                            <FiShield size={11} /> Administrator
                        </span>
                        {emailVerified && (
                            <span style={{
                                display: "inline-flex", alignItems: "center", gap: 5,
                                background: "#50C87818", color: "#27774a",
                                border: "1px solid #50C87840",
                                borderRadius: 999, padding: "3px 12px",
                                fontSize: 11, fontWeight: 700,
                            }}>
                                <FiCheck size={11} /> Verified
                            </span>
                        )}
                        <span style={{
                            display: "inline-flex", alignItems: "center",
                            background: providerMeta.bg, color: providerMeta.color,
                            border: `1px solid ${providerMeta.border}`,
                            borderRadius: 999, padding: "3px 12px",
                            fontSize: 11, fontWeight: 700,
                        }}>
                            {providerMeta.label}
                        </span>
                    </div>
                </div>
            </div>

            {/* ── Account details card ── */}
            <div style={{
                background: "#fff", borderRadius: 20,
                border: "1px solid #E0E0E0",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                padding: "6px 24px 6px",
                marginBottom: 16,
            }}>
                <DetailRow icon={<FiMail size={16} />} label="Email">
                    <a
                        href={`mailto:${email}`}
                        style={{ color: "#2a9d8f", textDecoration: "none", fontWeight: 600 }}
                    >
                        {email}
                    </a>
                </DetailRow>

                <DetailRow icon={<FiUser size={16} />} label="User ID">
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontSize: 12, fontFamily: "monospace", color: "#333", wordBreak: "break-all", flex: 1 }}>
                            {uid || "—"}
                        </span>
                        {uid && (
                            <button
                                onClick={handleCopy}
                                title={copied ? "Copied!" : "Copy UID"}
                                style={{
                                    display: "inline-flex", alignItems: "center", gap: 5,
                                    background: copied ? "#2a9d8f" : "#f7f9fb",
                                    color: copied ? "#fff" : "#606060",
                                    border: `1.5px solid ${copied ? "#2a9d8f" : "#E0E0E0"}`,
                                    borderRadius: 8, padding: "4px 10px",
                                    fontSize: 11, fontWeight: 700, cursor: "pointer",
                                    transition: "all 0.2s ease",
                                    flexShrink: 0,
                                }}
                            >
                                {copied ? <FiCheck size={12} /> : <FiCopy size={12} />}
                                {copied ? "Copied" : "Copy"}
                            </button>
                        )}
                    </div>
                </DetailRow>

                <DetailRow icon={<FiCalendar size={16} />} label="Account Created">
                    {createdAt}
                </DetailRow>

                <DetailRow icon={<FiClock size={16} />} label="Last Sign-in" isLast>
                    {lastSignIn}
                </DetailRow>
            </div>

            {/* ── Sign out ── */}
            <button
                onClick={handleSignOut}
                disabled={signingOut}
                className="primary-button"
                style={{
                    backgroundColor: "#e76f51", color: "#fff", borderColor: "#cf6348",
                    fontSize: 14, padding: "11px 24px", width: "100%",
                    justifyContent: "center",
                    opacity: signingOut ? 0.6 : 1,
                }}
            >
                <FiLogOut size={15} />
                {signingOut ? "Signing out…" : "Sign Out"}
            </button>
        </div>
    );
}
